import { z } from 'zod'
import { ACTIVITY_SOURCES, ACTIVITY_TYPES } from './activity'
import { LocationGeometrySchema, PlanOverlaySchema } from './geometry'
import { FloorPlanSchema } from './floor-plan'
import {
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_FREQUENCIES,
  NOTIFICATION_PRESETS,
} from './notifications'
import { HOUSEHOLD_ROLES, REGISTRATION_MODES } from './roles'
import { LOCALES } from './locales'
import { PAYMENT_STATUSES, PLAN_FEATURES, PLAN_INTERVALS, PLAN_LIMITS, SUBSCRIPTION_STATUSES } from './plans'

/** Schémas partagés par l'API (validation + OpenAPI) et les clients web / mobile. */

export const IdSchema = z.uuid()
export const IsoDateSchema = z.iso.datetime({ offset: true })
export const LocaleSchema = z.enum(LOCALES)
export const RoleSchema = z.enum(HOUSEHOLD_ROLES)
/** `web` : cookie de session HttpOnly. `mobile` : jetons access + refresh dans la réponse. */
export const ClientKindSchema = z.enum(['web', 'mobile'])

export const EmailSchema = z.string().trim().toLowerCase().pipe(z.email().max(254))
export const PasswordSchema = z.string().min(10).max(200)
export const DisplayNameSchema = z.string().trim().min(1).max(80)

// --- Auth -------------------------------------------------------------------------------------

export const RegisterBodySchema = z.object({
  email: EmailSchema,
  password: PasswordSchema,
  displayName: DisplayNameSchema,
  locale: LocaleSchema.optional(),
  /** Code d'accès à l'inscription (mode `code`). */
  accessCode: z.string().trim().min(1).max(64).optional(),
  /** Code d'invitation à un foyer : vaut aussi code d'accès. */
  invitationCode: z.string().trim().min(1).max(64).optional(),
  client: ClientKindSchema.default('web'),
})

export const LoginBodySchema = z.object({
  email: EmailSchema,
  password: z.string().min(1).max(200),
  client: ClientKindSchema.default('web'),
})

export const RefreshBodySchema = z.object({ refreshToken: z.string().min(20).max(200) })
export const EmailOnlyBodySchema = z.object({ email: EmailSchema })
export const TokenBodySchema = z.object({ token: z.string().min(20).max(200) })
export const ResetPasswordBodySchema = z.object({ token: z.string().min(20).max(200), password: PasswordSchema })
export const LoginLinkConsumeBodySchema = z
  .object({
    token: z.string().min(20).max(200).optional(),
    email: EmailSchema.optional(),
    code: z.string().regex(/^\d{6}$/).optional(),
    client: ClientKindSchema.default('web'),
  })
  .refine((b) => Boolean(b.token) || (Boolean(b.email) && Boolean(b.code)), { message: 'token_or_email_code' })

export const TotpCodeBodySchema = z.object({ code: z.string().regex(/^\d{6}$/) })
/** Code de l'application (6 chiffres) ou code de récupération (`XXXXX-XXXXX`, casse et tirets ignorés). */
export const MfaCodeBodySchema = z.object({ code: z.string().trim().min(6).max(20) })
export const RECOVERY_CODE_COUNT = 10
export const RecoveryCodesResponseSchema = z.object({ recoveryCodes: z.array(z.string()) })
export const MfaStatusSchema = z.object({ enabled: z.boolean(), recoveryCodesLeft: z.number().int() })
export type RecoveryCodesResponse = z.infer<typeof RecoveryCodesResponseSchema>
export type MfaStatus = z.infer<typeof MfaStatusSchema>

export const UserSchema = z.object({
  id: IdSchema,
  email: z.string(),
  displayName: z.string(),
  locale: LocaleSchema,
  emailVerified: z.boolean(),
  simplifiedMode: z.boolean(),
  isInstanceAdmin: z.boolean(),
  mfaEnabled: z.boolean(),
  createdAt: IsoDateSchema,
})
export type User = z.infer<typeof UserSchema>

export const MobileTokensSchema = z.object({
  accessToken: z.string(),
  accessTokenExpiresAt: IsoDateSchema,
  refreshToken: z.string(),
})

export const AuthResponseSchema = z.object({
  user: UserSchema,
  /** Présent seulement pour `client: mobile`. */
  tokens: MobileTokensSchema.optional(),
})
export type AuthResponse = z.infer<typeof AuthResponseSchema>

/** Suppression de son compte : adresse e-mail retapée, et mot de passe si le compte en a un. */
export const DeleteMyAccountBodySchema = z.object({
  confirmEmail: z.string().trim().min(3).max(320),
  password: z.string().max(200).optional(),
})
export type DeleteMyAccountBody = z.infer<typeof DeleteMyAccountBodySchema>

export const MeResponseSchema = z.object({
  user: UserSchema,
  /**
   * L'administrateur doit valider son code TOTP pour cette session avant d'accéder à /admin, sauf si l'instance
   * l'en dispense (`adminMfaRequired` faux : variable `ADMIN_REQUIRE_MFA=false`).
   */
  session: z.object({ mfaVerified: z.boolean(), adminMfaRequired: z.boolean().default(true) }),
})
export type MeResponse = z.infer<typeof MeResponseSchema>

export const UpdateMeBodySchema = z.object({
  displayName: DisplayNameSchema.optional(),
  locale: LocaleSchema.optional(),
  simplifiedMode: z.boolean().optional(),
})

export const TotpSetupResponseSchema = z.object({ otpauthUrl: z.string(), secret: z.string() })

export const PublicConfigSchema = z.object({
  registrationMode: z.enum(REGISTRATION_MODES),
  publicUrl: z.string(),
  locales: z.array(LocaleSchema),
  /** Page de confidentialité : `null` = pas encore renseigné par l'hébergeur de l'instance. */
  legal: z.object({
    publisher: z.string().nullable(),
    contactEmail: z.string().nullable(),
    host: z.string().nullable(),
  }),
  trashRetentionDays: z.number().int(),
  /** Passerelle IA (aide à l'aménagement) : configurée sur l'instance ? `provider` : hôte qui reçoit les demandes. */
  ai: z.object({ enabled: z.boolean(), provider: z.string().nullable() }),
})
export type PublicConfig = z.infer<typeof PublicConfigSchema>

export const OkSchema = z.object({ ok: z.literal(true) })

// --- Foyers -----------------------------------------------------------------------------------

export const HouseholdNameSchema = z.string().trim().min(1).max(80)

export const HouseholdSchema = z.object({
  id: IdSchema,
  name: z.string(),
  role: RoleSchema,
  readOnly: z.boolean(),
  createdAt: IsoDateSchema,
})
export type Household = z.infer<typeof HouseholdSchema>

export const CreateHouseholdBodySchema = z.object({ id: IdSchema.optional(), name: HouseholdNameSchema })
export const UpdateHouseholdBodySchema = z.object({ name: HouseholdNameSchema })

export const MemberSchema = z.object({
  userId: IdSchema,
  displayName: z.string(),
  email: z.string(),
  role: RoleSchema,
  joinedAt: IsoDateSchema,
})
export type Member = z.infer<typeof MemberSchema>
export const UpdateMemberBodySchema = z.object({ role: RoleSchema })

export const InvitationSchema = z.object({
  id: IdSchema,
  code: z.string(),
  url: z.string(),
  role: RoleSchema,
  maxUses: z.number().int().nullable(),
  uses: z.number().int(),
  expiresAt: IsoDateSchema.nullable(),
  revokedAt: IsoDateSchema.nullable(),
  createdAt: IsoDateSchema,
})
export type Invitation = z.infer<typeof InvitationSchema>

export const CreateInvitationBodySchema = z.object({
  role: RoleSchema.exclude(['owner']).default('member'),
  maxUses: z.number().int().min(1).max(100).nullable().default(1),
  expiresInDays: z.number().int().min(1).max(90).default(7),
})

export const InvitationPreviewSchema = z.object({
  householdName: z.string(),
  role: RoleSchema,
  invitedBy: z.string(),
})

export const LimitUsageSchema = z.object({ limit: z.number(), used: z.number() })
export const EntitlementsSchema = z.object({
  plan: z.object({ code: z.string(), name: z.string() }),
  readOnly: z.boolean(),
  features: z.record(z.enum(PLAN_FEATURES), z.boolean()),
  limits: z.record(z.enum(PLAN_LIMITS), LimitUsageSchema),
})
export type Entitlements = z.infer<typeof EntitlementsSchema>

// --- Administration ---------------------------------------------------------------------------

export const AdminStatsSchema = z.object({
  users: z.number().int(),
  activeUsers30d: z.number().int(),
  households: z.number().int(),
  storageBytes: z.number().int(),
  pendingJobs: z.number().int(),
  failedJobs: z.number().int(),
  recentUsers: z.array(z.object({ id: IdSchema, displayName: z.string(), email: z.string(), createdAt: IsoDateSchema })),
})

export const AdminUserSchema = z.object({
  id: IdSchema,
  email: z.string(),
  displayName: z.string(),
  locale: LocaleSchema,
  emailVerified: z.boolean(),
  isInstanceAdmin: z.boolean(),
  /** Code de sécurité (TOTP) activé. */
  mfaEnabled: z.boolean(),
  disabledAt: IsoDateSchema.nullable(),
  lastLoginAt: IsoDateSchema.nullable(),
  householdCount: z.number().int(),
  createdAt: IsoDateSchema,
})
export type AdminUser = z.infer<typeof AdminUserSchema>

export const AdminMembershipSchema = z.object({
  householdId: IdSchema,
  householdName: z.string(),
  role: RoleSchema,
})

export const AdminUserDetailSchema = AdminUserSchema.extend({
  storageBytes: z.number().int(),
  memberships: z.array(AdminMembershipSchema),
})
export type AdminUserDetail = z.infer<typeof AdminUserDetailSchema>

export const SubscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES)
export const PaymentStatusSchema = z.enum(PAYMENT_STATUSES)

export const AdminHouseholdSchema = z.object({
  id: IdSchema,
  name: z.string(),
  memberCount: z.number().int(),
  placeCount: z.number().int(),
  storageBytes: z.number().int(),
  planCode: z.string(),
  planName: z.string(),
  subscriptionStatus: SubscriptionStatusSchema.nullable(),
  forcedReadOnly: z.boolean(),
  overLimitSince: IsoDateSchema.nullable(),
  createdAt: IsoDateSchema,
})
export type AdminHousehold = z.infer<typeof AdminHouseholdSchema>

export const AdminHouseholdMemberSchema = z.object({
  userId: IdSchema,
  email: z.string(),
  displayName: z.string(),
  role: RoleSchema,
})

export const AdminSubscriptionSchema = z.object({
  id: IdSchema,
  householdId: IdSchema,
  planId: IdSchema,
  planCode: z.string(),
  planName: z.string(),
  status: SubscriptionStatusSchema,
  payerUserId: IdSchema.nullable(),
  currentPeriodStart: IsoDateSchema.nullable(),
  currentPeriodEnd: IsoDateSchema.nullable(),
  compedReason: z.string().nullable(),
  compedUntil: IsoDateSchema.nullable(),
  provider: z.string(),
  canceledAt: IsoDateSchema.nullable(),
  createdAt: IsoDateSchema,
  updatedAt: IsoDateSchema,
})
export type AdminSubscription = z.infer<typeof AdminSubscriptionSchema>

export const AdminHouseholdDetailSchema = AdminHouseholdSchema.extend({
  members: z.array(AdminHouseholdMemberSchema),
  usage: z.object({
    members: z.number().int(),
    places: z.number().int(),
    photoStorageMb: z.number().int(),
    photosPerItem: z.number().int(),
  }),
  limits: z.record(z.enum(PLAN_LIMITS), z.number()),
  features: z.record(z.enum(PLAN_FEATURES), z.boolean()),
  subscription: AdminSubscriptionSchema.nullable(),
})
export type AdminHouseholdDetail = z.infer<typeof AdminHouseholdDetailSchema>

export const UpsertPlanBodySchema = z.object({
  code: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9_-]{2,40}$/)
    .optional(),
  name: z.string().trim().min(1).max(80),
  limits: z.object({
    photoStorageMb: z.number().int().min(0).max(1_000_000_000),
    photosPerItem: z.number().int().min(0).max(1000),
    members: z.number().int().min(1).max(10_000),
    places: z.number().int().min(1).max(10_000),
    householdsPerUser: z.number().int().min(1).max(1000),
    items: z.number().int().min(1).max(10_000_000).default(20000),
    locations: z.number().int().min(1).max(10_000_000).default(5000),
  }),
  features: z.object({
    editor3d: z.boolean(),
    csvImport: z.boolean(),
    mcp: z.boolean(),
    aiInterpreter: z.boolean(),
    bulkLabels: z.boolean(),
  }),
  priceAmountCents: z.number().int().min(0).max(100_000_000).default(0),
  currency: z.string().trim().length(3).default('EUR'),
  interval: z.enum(PLAN_INTERVALS).nullable().default(null),
})

export const AssignSubscriptionBodySchema = z.object({
  planCode: z.string().trim().min(1).max(40),
  status: z.enum(['comped', 'active', 'free']).default('comped'),
  reason: z.string().trim().min(1).max(500),
  days: z.number().int().min(1).max(3650).nullable().default(null),
  payerUserId: IdSchema.nullable().optional(),
})

export const CreateManualPaymentBodySchema = z.object({
  householdId: IdSchema,
  amountExclTaxCents: z.number().int().min(0).max(100_000_000),
  taxRateBp: z.number().int().min(0).max(10_000).default(0),
  currency: z.string().trim().length(3).default('EUR'),
  status: PaymentStatusSchema.default('paid'),
  providerReference: z.string().trim().max(200).nullable().optional(),
  paidAt: IsoDateSchema.optional(),
})

export const AdminPaymentSchema = z.object({
  id: IdSchema,
  householdId: IdSchema,
  householdName: z.string(),
  subscriptionId: IdSchema.nullable(),
  amountExclTaxCents: z.number().int(),
  taxCents: z.number().int(),
  amountInclTaxCents: z.number().int(),
  taxRateBp: z.number().int(),
  currency: z.string(),
  invoiceNumber: z.string().nullable(),
  status: PaymentStatusSchema,
  provider: z.string(),
  providerReference: z.string().nullable(),
  paidAt: IsoDateSchema.nullable(),
  createdAt: IsoDateSchema,
})
export type AdminPayment = z.infer<typeof AdminPaymentSchema>

export const SetForcedReadOnlyBodySchema = z.object({ forcedReadOnly: z.boolean() })

export const PaginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().max(200).optional(),
})
export const pageOf = <T extends z.ZodType>(item: T) => z.object({ items: z.array(item), nextCursor: z.string().nullable() })

export const AccessCodeSchema = z.object({
  id: IdSchema,
  code: z.string(),
  note: z.string().nullable(),
  maxUses: z.number().int().nullable(),
  uses: z.number().int(),
  expiresAt: IsoDateSchema.nullable(),
  revokedAt: IsoDateSchema.nullable(),
  grantPlanCode: z.string().nullable(),
  grantDays: z.number().int().nullable(),
  createdAt: IsoDateSchema,
})
export type AccessCode = z.infer<typeof AccessCodeSchema>

export const CreateAccessCodeBodySchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9-]{6,32}$/)
    .optional(),
  note: z.string().trim().max(200).optional(),
  maxUses: z.number().int().min(1).max(10000).nullable().default(1),
  expiresInDays: z.number().int().min(1).max(3650).nullable().default(null),
  grantPlanCode: z.string().max(40).nullable().default(null),
  /** Durée de l'offre accordée ; `null` = à vie. */
  grantDays: z.number().int().min(1).max(3650).nullable().default(null),
})

export const PlanSchema = z.object({
  id: IdSchema,
  code: z.string(),
  name: z.string(),
  limits: z.record(z.enum(PLAN_LIMITS), z.number()),
  features: z.record(z.enum(PLAN_FEATURES), z.boolean()),
  priceAmountCents: z.number().int(),
  currency: z.string(),
  interval: z.enum(PLAN_INTERVALS).nullable(),
  isDefault: z.boolean(),
})
export type Plan = z.infer<typeof PlanSchema>

export const AuditLogSchema = z.object({
  id: IdSchema,
  actor: z.object({ id: IdSchema, displayName: z.string() }).nullable(),
  action: z.string(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  data: z.record(z.string(), z.unknown()),
  createdAt: IsoDateSchema,
})
export type AuditLog = z.infer<typeof AuditLogSchema>

/** Journal des échanges avec l'IA (administration) : une demande, son issue. */
export const AdminAiRequestSchema = z.object({
  id: IdSchema,
  createdAt: IsoDateSchema,
  user: z.object({ id: IdSchema, displayName: z.string(), email: z.string() }).nullable(),
  household: z.object({ id: IdSchema, name: z.string() }),
  provider: z.string().nullable(),
  model: z.string(),
  prompt: z.string(),
  status: z.enum(['proposal', 'reply', 'error']),
  reply: z.string().nullable(),
  error: z.string().nullable(),
  proposalId: IdSchema.nullable(),
  durationMs: z.number().int(),
})
export type AdminAiRequest = z.infer<typeof AdminAiRequestSchema>
/** Détail : en plus, les messages envoyés (plan du lieu compris) et les réponses brutes du fournisseur. */
export const AdminAiRequestDetailSchema = AdminAiRequestSchema.extend({
  messages: z.array(z.unknown()),
  responses: z.array(z.unknown()),
})
export type AdminAiRequestDetail = z.infer<typeof AdminAiRequestDetailSchema>

// --- Inventaire (rangements et objets) --------------------------------------------------------

export const LocationTypeSchema = z.enum(['place', 'room', 'furniture', 'storage'])
export type LocationType = z.infer<typeof LocationTypeSchema>

export const LocationNameSchema = z.string().trim().min(1).max(120)

export const LocationSchema = z.object({
  id: IdSchema,
  parentId: IdSchema.nullable(),
  name: z.string(),
  type: LocationTypeSchema,
  notes: z.string().nullable(),
  position: z.number().int(),
  depth: z.number().int(),
  gridRow: z.number().int().nullable(),
  gridCol: z.number().int().nullable(),
  publicCode: z.string(),
  /** Étage (pièces) ; null = non placé en 3D / hors plan. */
  floor: z.number().int().nullable(),
  /** Géométrie 3D (pièces / meubles) ; null si non modélisé. */
  geometry: LocationGeometrySchema.nullable(),
  /** Calque plan photo (lieux / places) : position et échelle au sol. */
  planOverlay: PlanOverlaySchema.nullable().optional(),
  /** Plan dessiné (lieux / places) : murs, portes, fenêtres par étage. */
  floorPlan: FloorPlanSchema.nullable().optional(),
  revision: z.number().int(),
  createdAt: IsoDateSchema,
  updatedAt: IsoDateSchema,
  /** Première photo du rangement, si présente. */
  primaryPhotoId: IdSchema.nullable().optional(),
  /** Nombre d'objets directement dans ce rangement (contenu d'un niveau). */
  itemCount: z.number().int().nonnegative().optional(),
})
export type Location = z.infer<typeof LocationSchema>

export const LocationPathSegmentSchema = z.object({
  id: IdSchema,
  name: z.string(),
  depth: z.number().int(),
  /** Type du nœud (pour fly-to 3D : place / room / furniture / storage). */
  type: LocationTypeSchema,
  /** Étage (pièces) ; null sinon. */
  floor: z.number().int().nullable().optional(),
})
export type LocationPathSegment = z.infer<typeof LocationPathSegmentSchema>

/** Résolution d'un QR `/r/<publicCode>` (membre du foyer uniquement). */
export const ResolvePublicCodeResponseSchema = z.object({
  householdId: IdSchema,
  location: LocationSchema,
})
export type ResolvePublicCodeResponse = z.infer<typeof ResolvePublicCodeResponseSchema>

export const TrashEntrySchema = z.object({
  kind: z.enum(['location', 'item']),
  id: IdSchema,
  name: z.string(),
  deletedAt: IsoDateSchema,
  /** Nombre de rangements enfants restaurés avec (sous-arbre). */
  subtreeCount: z.number().int().nonnegative().optional(),
})
export type TrashEntry = z.infer<typeof TrashEntrySchema>

export const TrashListResponseSchema = z.object({
  items: z.array(TrashEntrySchema),
  retentionDays: z.number().int().positive(),
})
export type TrashListResponse = z.infer<typeof TrashListResponseSchema>

export const CsvImportBodySchema = z.object({
  /** Contenu texte du CSV (UTF-8). */
  csv: z.string().min(1).max(2_000_000),
})
export type CsvImportBody = z.infer<typeof CsvImportBodySchema>

export const CsvImportRowPreviewSchema = z.object({
  line: z.number().int().positive(),
  ok: z.boolean(),
  name: z.string().nullable(),
  locationPath: z.string(),
  quantity: z.number().int().positive().nullable(),
  errors: z.array(z.string()),
  locationsToCreate: z.array(z.string()),
})
export type CsvImportRowPreview = z.infer<typeof CsvImportRowPreviewSchema>

export const CsvImportPreviewResponseSchema = z.object({
  delimiter: z.string(),
  rows: z.array(CsvImportRowPreviewSchema),
  okCount: z.number().int().nonnegative(),
  errorCount: z.number().int().nonnegative(),
  locationsToCreateCount: z.number().int().nonnegative(),
})
export type CsvImportPreviewResponse = z.infer<typeof CsvImportPreviewResponseSchema>

export const CsvImportApplyResponseSchema = z.object({
  createdItems: z.number().int().nonnegative(),
  createdLocations: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
})
export type CsvImportApplyResponse = z.infer<typeof CsvImportApplyResponseSchema>

export const MovementSchema = z.object({
  id: IdSchema,
  itemId: IdSchema,
  fromLocationId: IdSchema.nullable(),
  toLocationId: IdSchema.nullable(),
  fromLocationName: z.string().nullable(),
  toLocationName: z.string().nullable(),
  userId: IdSchema.nullable(),
  displayName: z.string().nullable(),
  movedAt: IsoDateSchema,
})
export type Movement = z.infer<typeof MovementSchema>

export const LoanSchema = z.object({
  id: IdSchema,
  itemId: IdSchema,
  borrower: z.string(),
  lentAt: IsoDateSchema,
  /** Date de retour prévue (`YYYY-MM-DD`) ou null. */
  dueAt: z.string().nullable(),
  returnedAt: IsoDateSchema.nullable(),
  createdAt: IsoDateSchema,
})
export type Loan = z.infer<typeof LoanSchema>

export const CreateLoanBodySchema = z.object({
  borrower: z.string().trim().min(1).max(120),
  dueAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
})
export type CreateLoanBody = z.infer<typeof CreateLoanBodySchema>

/** Étiquette imprimable : URL stable + nom (le QR ne contient que l'URL). */
export const LocationLabelSchema = z.object({
  id: IdSchema,
  name: z.string(),
  publicCode: z.string(),
  url: z.string(),
})
export type LocationLabel = z.infer<typeof LocationLabelSchema>

export const LocationLabelsResponseSchema = z.object({
  items: z.array(LocationLabelSchema),
  /** Domaine utilisé dans les QR (doit rester stable). */
  publicUrl: z.string(),
})
export type LocationLabelsResponse = z.infer<typeof LocationLabelsResponseSchema>

export const CreateLocationBodySchema = z.object({
  id: IdSchema.optional(),
  parentId: IdSchema.nullable().optional(),
  name: LocationNameSchema,
  type: LocationTypeSchema.optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
})

/** Création de rangements en série (grille ou liste numérotée). Plafond : 100. */
export const CreateLocationsBatchBodySchema = z.discriminatedUnion('mode', [
  z
    .object({
      mode: z.literal('grid'),
      parentId: IdSchema,
      rows: z.number().int().min(1).max(50),
      cols: z.number().int().min(1).max(50),
      prefix: z.string().trim().max(40).optional(),
      type: LocationTypeSchema.optional(),
    })
    .refine((b) => b.rows * b.cols <= 100, { message: 'batch_too_large' }),
  z.object({
    mode: z.literal('series'),
    parentId: IdSchema,
    prefix: z.string().trim().min(1).max(80),
    count: z.number().int().min(1).max(100),
    start: z.number().int().min(0).max(10_000).optional(),
    naming: z.enum(['number', 'letter']).default('number'),
    type: LocationTypeSchema.optional(),
  }),
])
export type CreateLocationsBatchBody = z.infer<typeof CreateLocationsBatchBodySchema>

export const CreateLocationsBatchResponseSchema = z.object({
  items: z.array(LocationSchema),
})

export const UpdateLocationBodySchema = z.object({
  name: LocationNameSchema.optional(),
  type: LocationTypeSchema.optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  position: z.number().int().min(0).max(100_000).optional(),
  floor: z.number().int().min(-5).max(50).nullable().optional(),
  geometry: LocationGeometrySchema.nullable().optional(),
  planOverlay: PlanOverlaySchema.nullable().optional(),
  floorPlan: FloorPlanSchema.nullable().optional(),
})

/** Scène 3D d'un lieu (place) pour un étage : pièces + meubles avec géométrie. */
export const SceneLocationSchema = LocationSchema.extend({
  geometry: LocationGeometrySchema,
  /** Étage où la zone est dessinée (celui de la pièce pour un meuble). */
  sceneFloor: z.number().int(),
})
export type SceneLocation = z.infer<typeof SceneLocationSchema>

/**
 * Bâtiment d'un terrain (décision § 13, option A) : lieu enfant du lieu affiché, avec son emprise (`place.geometry`,
 * boîte dans le repère du terrain ; absente tant qu'il n'est pas posé), ses étages et ses pièces / meubles de tous
 * les étages, dans son propre repère.
 */
export const SceneBuildingSchema = z.object({
  place: LocationSchema,
  floors: z.array(z.number().int()),
  locations: z.array(SceneLocationSchema),
})
export type SceneBuilding = z.infer<typeof SceneBuildingSchema>

export const HouseholdSceneSchema = z.object({
  place: LocationSchema,
  floor: z.number().int(),
  floors: z.array(z.number().int()),
  locations: z.array(SceneLocationSchema),
  /** Bâtiments du terrain (lieux enfants de ce lieu) ; vide pour un lieu sans bâtiment. */
  buildings: z.array(SceneBuildingSchema).optional(),
  /** Terrain qui contient ce lieu (lieu parent), s'il y en a un : retour à « toute la propriété ». */
  terrain: LocationSchema.nullable().optional(),
})
export type HouseholdScene = z.infer<typeof HouseholdSceneSchema>

export const HouseholdSceneQuerySchema = z.object({
  placeId: IdSchema,
  floor: z.coerce.number().int().optional(),
  /** `1` : pièces et meubles de tous les étages (vue maison entière, étage du dessous en transparence). */
  allFloors: z.enum(['0', '1']).optional(),
})

export const MoveLocationBodySchema = z.object({
  parentId: IdSchema.nullable(),
})

export const ItemSchema = z.object({
  id: IdSchema,
  locationId: IdSchema.nullable(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  quantity: z.number().int(),
  tags: z.array(z.string()),
  brand: z.string().nullable(),
  model: z.string().nullable(),
  serialNumber: z.string().nullable(),
  purchasePriceCents: z.number().int().nullable(),
  purchaseCurrency: z.string().nullable(),
  purchaseDate: z.string().nullable(),
  purchasePlace: z.string().nullable(),
  warrantyEnd: z.string().nullable(),
  revision: z.number().int(),
  createdAt: IsoDateSchema,
  updatedAt: IsoDateSchema,
  /** Première photo de l'objet, si présente. */
  primaryPhotoId: IdSchema.nullable().optional(),
})
export type Item = z.infer<typeof ItemSchema>

export const PhotoThumbStatusSchema = z.enum(['pending', 'ready', 'failed'])
export type PhotoThumbStatus = z.infer<typeof PhotoThumbStatusSchema>

export const PhotoSchema = z.object({
  id: IdSchema,
  itemId: IdSchema.nullable(),
  locationId: IdSchema.nullable(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  position: z.number().int(),
  /** Miniature WebP : `pending` jusqu'au traitement en file d'attente. */
  thumbStatus: PhotoThumbStatusSchema.optional(),
  createdAt: IsoDateSchema,
})
export type Photo = z.infer<typeof PhotoSchema>

export const AttachmentKindSchema = z.enum(['invoice', 'manual', 'warranty', 'other'])
export type AttachmentKind = z.infer<typeof AttachmentKindSchema>

export const AttachmentSchema = z.object({
  id: IdSchema,
  itemId: IdSchema,
  kind: AttachmentKindSchema,
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  revision: z.number().int(),
  createdAt: IsoDateSchema,
  updatedAt: IsoDateSchema,
})
export type Attachment = z.infer<typeof AttachmentSchema>

/** Objet avec son chemin de rangement (recherche et fiche). */
export const ItemWithPathSchema = ItemSchema.extend({
  path: z.array(LocationPathSegmentSchema),
  photos: z.array(PhotoSchema).optional(),
  attachments: z.array(AttachmentSchema).optional(),
})
export type ItemWithPath = z.infer<typeof ItemWithPathSchema>

export const CreateItemBodySchema = z.object({
  id: IdSchema.optional(),
  /** `null` ou omis → « À classer ». */
  locationId: IdSchema.nullable().optional(),
  name: z.string().trim().min(1).max(200).nullable().optional(),
  description: z.string().trim().max(4000).nullable().optional(),
  quantity: z.number().int().min(1).max(100_000).optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
})

export const UpdateItemBodySchema = z.object({
  name: z.string().trim().min(1).max(200).nullable().optional(),
  description: z.string().trim().max(4000).nullable().optional(),
  quantity: z.number().int().min(1).max(100_000).optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  brand: z.string().trim().max(120).nullable().optional(),
  model: z.string().trim().max(120).nullable().optional(),
  serialNumber: z.string().trim().max(120).nullable().optional(),
  purchasePriceCents: z.number().int().min(0).nullable().optional(),
  purchaseCurrency: z.string().trim().length(3).nullable().optional(),
  purchaseDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  purchasePlace: z.string().trim().max(200).nullable().optional(),
  warrantyEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
})

export const MoveItemBodySchema = z.object({
  locationId: IdSchema.nullable(),
})

export const LocationContentsSchema = z.object({
  path: z.array(LocationPathSegmentSchema),
  locations: z.array(LocationSchema),
  items: z.array(ItemSchema),
  /** Nombre d'objets « À classer » (uniquement à la racine). */
  uncategorizedCount: z.number().int().optional(),
})
export type LocationContents = z.infer<typeof LocationContentsSchema>

const queryBool = z
  .union([z.literal('true'), z.literal('false'), z.boolean()])
  .optional()
  .transform((v) => (v === undefined ? undefined : v === true || v === 'true'))

export const SearchItemsQuerySchema = PaginationQuerySchema.extend({
  q: z.string().trim().max(200).optional(),
  /** Filtrer sur un rangement et tout son sous-arbre. */
  locationId: IdSchema.optional(),
  /** true = seulement les objets sans rangement. */
  uncategorized: queryBool,
  /** true = objets sans nom (à compléter). */
  incomplete: queryBool,
})

// --- Téléphones (notifications push) ---------------------------------------------------------------

export const DevicePlatformSchema = z.enum(['ios', 'android'])
export const RegisterDeviceBodySchema = z.object({
  /** Jeton Expo Push (`ExponentPushToken[…]`). */
  pushToken: z.string().trim().min(10).max(300),
  platform: DevicePlatformSchema,
  locale: LocaleSchema.optional(),
})
export type RegisterDeviceBody = z.infer<typeof RegisterDeviceBodySchema>
export const DeviceSchema = z.object({
  id: IdSchema,
  platform: DevicePlatformSchema,
  locale: LocaleSchema,
  lastSeenAt: IsoDateSchema,
  createdAt: IsoDateSchema,
})
export type Device = z.infer<typeof DeviceSchema>

export const NotificationsQuerySchema = PaginationQuerySchema.extend({
  unreadOnly: queryBool,
})

/** Curseur de synchronisation incrémentale (séquence de modification par foyer). */
export const SyncChangesQuerySchema = z.object({
  since: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(100),
})

export const SyncChangeSchema = z.object({
  entityType: z.enum(['location', 'item', 'photo', 'attachment']),
  entityId: IdSchema,
  changeSeq: z.number().int().nonnegative(),
  deleted: z.boolean(),
  location: LocationSchema.optional(),
  item: ItemSchema.optional(),
  photo: PhotoSchema.optional(),
  attachment: AttachmentSchema.optional(),
})
export type SyncChange = z.infer<typeof SyncChangeSchema>

export const SyncChangesResponseSchema = z.object({
  /** Curseur à renvoyer au prochain appel (`since`). */
  cursor: z.number().int().nonnegative(),
  changes: z.array(SyncChangeSchema),
  hasMore: z.boolean(),
})
export type SyncChangesResponse = z.infer<typeof SyncChangesResponseSchema>

export const ActivityTypeSchema = z.enum(ACTIVITY_TYPES)
export const ActivitySourceSchema = z.enum(ACTIVITY_SOURCES)

export const ActivityEventSchema = z.object({
  id: IdSchema,
  type: ActivityTypeSchema,
  source: ActivitySourceSchema,
  userId: IdSchema.nullable(),
  displayName: z.string().nullable(),
  entityType: z.string().nullable(),
  entityId: IdSchema.nullable(),
  data: z.record(z.string(), z.unknown()),
  createdAt: IsoDateSchema,
})
export type ActivityEvent = z.infer<typeof ActivityEventSchema>

export const ActivityQuerySchema = PaginationQuerySchema.extend({
  userId: IdSchema.optional(),
  type: ActivityTypeSchema.optional(),
  locationId: IdSchema.optional(),
})

/** Signal SSE « du nouveau dans le foyer » (le détail passe par `/changes`). */
export const HouseholdRealtimeEventSchema = z.object({
  at: IsoDateSchema,
})

// --- Notifications ----------------------------------------------------------------------------

export const NotificationCategorySchema = z.enum(NOTIFICATION_CATEGORIES)
export const NotificationChannelSchema = z.enum(NOTIFICATION_CHANNELS)
export const NotificationFrequencySchema = z.enum(NOTIFICATION_FREQUENCIES)
export const NotificationPresetSchema = z.enum(NOTIFICATION_PRESETS)

export const NotificationSchema = z.object({
  id: IdSchema,
  householdId: IdSchema,
  type: NotificationCategorySchema,
  eventIds: z.array(IdSchema),
  data: z.record(z.string(), z.unknown()),
  readAt: IsoDateSchema.nullable(),
  createdAt: IsoDateSchema,
})
export type Notification = z.infer<typeof NotificationSchema>

export const NotificationsListResponseSchema = z.object({
  items: z.array(NotificationSchema),
  nextCursor: z.string().nullable(),
  unreadCount: z.number().int().nonnegative(),
})
export type NotificationsListResponse = z.infer<typeof NotificationsListResponseSchema>

export const NotificationPreferenceSchema = z.object({
  type: NotificationCategorySchema,
  channel: NotificationChannelSchema,
  frequency: NotificationFrequencySchema,
})
export type NotificationPreference = z.infer<typeof NotificationPreferenceSchema>

export const NotificationPreferencesResponseSchema = z.object({
  items: z.array(NotificationPreferenceSchema),
  /** Preset dérivé si les lignes correspondent à un choix simplifié. */
  preset: NotificationPresetSchema.nullable(),
})
export type NotificationPreferencesResponse = z.infer<typeof NotificationPreferencesResponseSchema>

export const UpdateNotificationPreferencesBodySchema = z.union([
  z.object({ preset: NotificationPresetSchema }),
  z.object({ items: z.array(NotificationPreferenceSchema).min(1).max(20) }),
])
export type UpdateNotificationPreferencesBody = z.infer<typeof UpdateNotificationPreferencesBodySchema>

/** Jetons d'accès personnels (MCP / intégrations). */
export const ApiTokenScopeSchema = z.enum(['read', 'read_write'])
export type ApiTokenScope = z.infer<typeof ApiTokenScopeSchema>

export const ApiTokenSchema = z.object({
  id: IdSchema,
  householdId: IdSchema,
  name: z.string(),
  scope: ApiTokenScopeSchema,
  expiresAt: IsoDateSchema.nullable(),
  lastUsedAt: IsoDateSchema.nullable(),
  revokedAt: IsoDateSchema.nullable(),
  createdAt: IsoDateSchema,
})
export type ApiToken = z.infer<typeof ApiTokenSchema>

export const CreateApiTokenBodySchema = z.object({
  name: z.string().trim().min(1).max(80),
  scope: ApiTokenScopeSchema.default('read_write'),
  /** Jours de validité ; null = sans expiration. */
  expiresInDays: z.number().int().min(1).max(3650).nullable().optional(),
})
export type CreateApiTokenBody = z.infer<typeof CreateApiTokenBodySchema>

/** Réponse de création : le secret n'est montré qu'une fois. */
export const CreateApiTokenResponseSchema = ApiTokenSchema.extend({
  token: z.string().min(20),
})
export type CreateApiTokenResponse = z.infer<typeof CreateApiTokenResponseSchema>

export const InterpretCommandBodySchema = z.object({
  text: z.string().trim().min(1).max(500),
})
export type InterpretCommandBody = z.infer<typeof InterpretCommandBodySchema>

export const CommandCandidateSchema = z.object({
  id: IdSchema,
  name: z.string(),
  label: z.string().optional(),
  score: z.number(),
})
export type CommandCandidate = z.infer<typeof CommandCandidateSchema>

export const CommandProposalSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('find_item'),
    item: CommandCandidateSchema,
  }),
  z.object({
    type: z.literal('move_item'),
    item: CommandCandidateSchema,
    location: CommandCandidateSchema,
    from: CommandCandidateSchema.optional(),
  }),
  z.object({
    type: z.literal('lend_item'),
    item: CommandCandidateSchema,
    borrower: z.string().min(1).max(120),
  }),
  z.object({
    type: z.literal('return_item'),
    item: CommandCandidateSchema,
  }),
])
export type CommandProposal = z.infer<typeof CommandProposalSchema>

export const InterpretCommandResponseSchema = z.object({
  status: z.enum(['ok', 'ambiguous', 'unknown', 'unsupported']),
  proposal: CommandProposalSchema.optional(),
  alternatives: z.array(CommandProposalSchema).optional(),
  /** Clé i18n optionnelle (ex. commands.unsupported). */
  reason: z.string().optional(),
})
export type InterpretCommandResponse = z.infer<typeof InterpretCommandResponseSchema>

export const ApplyCommandBodySchema = z.object({
  proposal: CommandProposalSchema,
})
export type ApplyCommandBody = z.infer<typeof ApplyCommandBodySchema>

export const ApplyCommandResponseSchema = z.object({
  ok: z.literal(true),
  /** Pour find_item : chemin de l'objet. */
  path: z.array(LocationPathSegmentSchema).optional(),
  itemId: IdSchema.optional(),
  locationId: IdSchema.nullable().optional(),
})
export type ApplyCommandResponse = z.infer<typeof ApplyCommandResponseSchema>
